import {
  Calendar as GraphCalendar,
  Event as GraphEvent,
  OnlineMeeting,
} from '@microsoft/microsoft-graph-types';
import { chunk, get, pick } from 'lodash';

import { MNSproCloudApi } from '../../mnspro-cloud';
import { Appointment, Calendar, CalendarType } from '../../mnspro-cloud/api/v2';
import { createGraphApi } from '../factory';
import {
  BatchResponseMultipleValues,
  BatchResponseSingleValue,
  ValueOf,
} from '../helper/helper';

type CalendarEventMap = { [key: string]: GraphEvent[] };

export type CalendarStub = { id: string; type: string };

// extends Omit<GraphCalendar, 'hexColor'>
export interface CalendarProps extends GraphCalendar {
  type: CalendarType;
  groupId: string | undefined;
}

const getBaseUrl = (calendar: CalendarStub): string => {
  return calendar.type === CalendarType.user ? '/me/calendars' : '/groups';
};

export class CalendarService {
  static async getUserCalendars(authToken: string): Promise<GraphCalendar[]> {
    const response = await createGraphApi(authToken).get<
      ValueOf<GraphCalendar[]>
    >('/me/calendars?$top999');

    return response.data.value;
  }

  static async getGroupCalendars(
    authToken: string,
    courseIds: string[],
  ): Promise<GraphCalendar[]> {
    const courseCalendars: GraphCalendar[] = [];

    for (const piece of chunk(courseIds, 10)) {
      const payload = {
        requests: piece.map(id => ({
          id,
          method: 'GET',
          url: `/groups/${id}/calendar`,
        })),
      };

      const batchResponse = await createGraphApi(authToken).post<
        BatchResponseSingleValue<GraphCalendar>
      >('/$batch', payload);

      for (const singleResponse of batchResponse.data.responses) {
        if (singleResponse.status !== 200) {
          continue;
        }

        const renamed = {
          ...singleResponse.body,
          // We need to overwrite the id here, since followup requests (ie to
          // retrieve the calendar events) do not need the calendar-id but
          // the group id :/
          id: singleResponse.id,
          // All group calendars have name "Calendar" by default
          // rename the calendar internally to the group name
          // so they are distinguishable in the ui
          name: singleResponse.body?.owner?.name || '',
        } as GraphCalendar;

        courseCalendars.push(renamed);
      }
    }

    return courseCalendars;
  }

  /**
   * Retrieves a single event identified by its id
   */
  static async fetchEventById(
    authToken: string,
    eventId: string,
    calendarType: CalendarType,
    groupId?: string,
  ): Promise<GraphEvent> {
    const url =
      calendarType === CalendarType.group
        ? `/groups/${groupId}/calendar/events/${eventId}`
        : `/me/calendar/events/${eventId}`;

    const response = await createGraphApi(authToken).get<GraphEvent>(url);

    return response.data;
  }

  /**
   * Update an onlineMeeting
   */
  static async updateOnlineMeeting(
    authToken: string,
    meeting: OnlineMeeting,
    joinUrl: string,
  ): Promise<OnlineMeeting> {
    const onlineMeetingResponse = await createGraphApi(authToken).get<
      ValueOf<OnlineMeeting[]>
    >(`/me/onlineMeetings?$filter=JoinWebUrl eq '${joinUrl}'`);

    if (!onlineMeetingResponse.data.value) {
      throw new Error(`No OnlineMeeting found for joinWebUrl ${joinUrl}`);
    }

    const response = await createGraphApi(authToken).patch<OnlineMeeting>(
      `/me/onlineMeetings/${onlineMeetingResponse.data.value[0].id}`,
      meeting,
    );

    return response.data;
  }

  /**
   * Update an onlineMeeting
   */
  static async getOnlineMeeting(
    authToken: string,
    joinUrl: string,
  ): Promise<OnlineMeeting> {
    const onlineMeetingResponse = await createGraphApi(authToken).get<
      ValueOf<OnlineMeeting[]>
    >(`/me/onlineMeetings?$filter=JoinWebUrl eq '${joinUrl}'`);

    if (!onlineMeetingResponse.data.value) {
      throw new Error(`No OnlineMeeting found for joinWebUrl ${joinUrl}`);
    }

    return onlineMeetingResponse.data.value[0];
  }

  /**
   * Fetches multiple graph events, identified by id, in combined batch requests
   */
  static async fetchEventsById(
    authToken: string,
    eventIds: string[],
    calendarType: 'group' | 'personal' = 'personal',
    groupId?: string,
  ): Promise<GraphEvent[]> {
    const events: GraphEvent[] = [];

    for (const piece of chunk(eventIds, 4)) {
      // Every chunk value > 4 results in some requests returnig ApplicationThrottled
      const payload = {
        requests: piece.map(id => {
          const url =
            calendarType === 'personal'
              ? `/me/calendar/events/${id}`
              : `/groups/${groupId}/calendar/events/${id}`;

          return {
            id,
            method: 'GET',
            url,
          };
        }),
      };

      const batchResponse = await createGraphApi(authToken).post<
        BatchResponseSingleValue<GraphEvent>
      >('/$batch', payload);

      for (const singleResponse of batchResponse.data.responses) {
        if (singleResponse.status !== 200) {
          continue;
        }

        events.push(singleResponse.body);
      }
    }

    return events;
  }

  /**
   * Returns all events belonging to the given calendars
   */
  static async fetchEvents(
    authToken: string,
    calendars: { id: string; type: string }[],
    dateRangeStart: string,
    dateRangeEnd: string,
  ): Promise<CalendarEventMap> {
    const calendarMap: CalendarEventMap = {};

    // Chunk size of 4 matches Microsoft Graph's mailbox concurrency limit
    // This prevents ApplicationThrottled errors (HTTP 429)
    for (const piece of chunk(calendars, 4)) {
      const payload = {
        requests: piece.map(calendar => {
          const baseUrl =
            calendar.type === CalendarType.user ? '/me/calendars' : '/groups';

          return {
            id: calendar.id,
            method: 'GET',
            url: `${baseUrl}/${calendar.id}/calendarView?startdatetime=${dateRangeStart}&enddatetime=${dateRangeEnd}&$top=999`,
          };
        }),
      };

      const batchResponse = await createGraphApi(authToken).post<
        BatchResponseMultipleValues<GraphEvent[]>
      >('/$batch', payload);

      for (const singleResponse of batchResponse.data.responses) {
        calendarMap[singleResponse.id] = singleResponse.body.value ?? [];
      }
    }

    return calendarMap;
  }

  static async deleteEvent(
    authToken: string,
    eventId: string,
    calendar: CalendarStub,
  ): Promise<void> {
    //delete my events:
    ///me/calendar/events/{id}
    //delete group events:
    ///groups/{id}/calendar/events/{id}/
    const apiUrl =
      calendar.type === CalendarType.user
        ? `/me/calendar/events/${eventId}`
        : `/groups/${calendar.id}/calendar/events/${eventId}/`;
    await createGraphApi(authToken).delete<void>(apiUrl);
  }

  static async createEvent(
    authToken: string,
    event: GraphEvent,
    calendar: CalendarStub,
  ): Promise<GraphEvent> {
    const baseUrl = getBaseUrl(calendar);

    const response = await createGraphApi(authToken).post<GraphEvent>(
      `${baseUrl}/${calendar.id}/events/`,
      pick(event, [
        'subject',
        'start',
        'end',
        'recurrence',
        'body',
        'attendees',
        'isAllDay',
        'isOnlineMeeting',
        'onlineMeetingProvider',
      ]),
    );

    return response.data as GraphEvent;
  }

  static async patchEvent(
    authToken: string,
    event: GraphEvent,
    calendar: CalendarStub,
    changeSet: string[],
  ): Promise<GraphEvent> {
    if (changeSet.length === 0) {
      return event;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const body: { [key: string]: any } = {
      '@odata.context': get(event, '@odata.context'),
      '@odata.etag': get(event, '@odata.etag'),
    };

    for (const item of changeSet) {
      body[item] = get(event, item);
    }

    const baseUrl =
      calendar.type === CalendarType.user
        ? '/me'
        : `/groups/${calendar.id}/calendar`;

    const response = await createGraphApi(authToken).patch<GraphEvent>(
      `${baseUrl}/events/${event.id}`,
      body,
    );

    return response.data as GraphEvent;
  }

  private static createMNSproCalendarApi(authToken: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.CalendarsApiFactory(config);
    return api;
  }

  static async getAll(
    authToken: string,
    periodName: string,
    organizationId: string,
  ): Promise<CalendarProps[]> {
    const api = CalendarService.createMNSproCalendarApi(authToken);
    const response = await api.getAll(periodName, organizationId);
    const calendars: Calendar[] = response.data;

    return convertCalendarsToProps(calendars);
  }

  static async getSchoolCalendarAppointments(
    authToken: string,
    organizationId: string,
    start?: string,
    end?: string,
  ): Promise<Appointment[]> {
    const api = CalendarService.createMNSproCalendarApi(authToken);
    const response = await api.getAllParentalCommunicationAppointmentsOfMe(
      organizationId,
      undefined, // timePeriod
      start,
      end,
    );

    return response.data;
  }
}

export function convertCalendarsToProps(
  calendars: Calendar[],
): CalendarProps[] {
  return calendars.map(calendar => ({
    id: calendar.type === CalendarType.group ? calendar.groupId : calendar.id,
    canEdit: calendar.canEdit ?? null,
    color: 'auto',
    isDefaultCalendar: calendar.isDefaultCalendar ?? null,
    name: calendar.name ?? null,
    owner: { address: calendar.owner, name: calendar.owner },
    type: calendar.type ?? CalendarType.unknown, // ?? CalendarType.unknown,
    groupId: calendar.groupId,
    allowedOnlineMeetingProviders: null,
    canShare: null,
    canViewPrivateItems: null,
    changeKey: null,
    defaultOnlineMeetingProvider: null,
    isRemovable: null,
    isTallyingResponses: null,
    calendarPermissions: null,
    calendarView: null,
    events: null,
    multiValueExtendedProperties: null,
    singleValueExtendedProperties: null,
  }));
}
